// src/pages/StationaryManager.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SummaryApi from "../../../common";

export default function StationaryManager() {
  const [stationaries, setStationaries] = useState([]);
  const [schools, setSchools] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [useExistingSchool, setUseExistingSchool] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    schoolName: "",
    contact1: "",
    contact2: "",
    projectName: "",
    amount: "",
    address: "",
  });

  const navigate = useNavigate();

  // Fetch all stationaries
  const fetchStationaries = async () => {
    try {
      const res = await fetch(SummaryApi.stationaries.getAll.url, {
        method: SummaryApi.stationaries.getAll.method,
      });
      const data = await res.json();
      setStationaries(data);
    } catch (err) {
      console.error("Error fetching stationaries:", err);
    }
  };

  // Fetch all schools
  const fetchSchools = async () => {
    try {
      const res = await fetch(SummaryApi.schools.getAll.url);
      const data = await res.json();
      setSchools(data);
    } catch (err) {
      console.error("Error fetching Schools:", err);
    }
  };

  // Fetch all transactions
  const fetchTransactions = async () => {
    try {
      const res = await fetch(SummaryApi.stationaryTransactions.getAll.url);
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  useEffect(() => {
    fetchStationaries();
    fetchSchools();
    fetchTransactions();
  }, []);

const getPaidAmount = (stationaryId) => {
  const stationaryTransactions = transactions.filter(
    (t) => t.stationaryId?._id === stationaryId
  );

  return stationaryTransactions.reduce((total, transaction) => {
    const paidSum = transaction.installments
      ?.filter((inst) => inst.status === "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + paidSum;
  }, 0);
};

const getPendingAmount = (stationaryId) => {
  const stationaryTransactions = transactions.filter(
    (t) => t.stationaryId?._id === stationaryId
  );

  return stationaryTransactions.reduce((total, transaction) => {
    const pendingSum = transaction.installments
      ?.filter((inst) => inst.status !== "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + pendingSum;
  }, 0);
};


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSchoolSelect = (e) => {
    const schoolId = e.target.value;
    setSelectedSchoolId(schoolId);

    if (schoolId) {
      const school = schools.find((s) => s._id === schoolId);
      if (school) {
        // Pre-fill form with school data, but keep it editable
        setForm({
          schoolName: school.schoolName,
          contact1: school.contact1,
          contact2: school.contact2 || "",
          projectName: school.projectName,
          amount: school.amount,
          address: school.address,
        });
      }
    } else {
      // Reset form if no school selected
      setForm({
        schoolName: "",
        contact1: "",
        contact2: "",
        projectName: "",
        amount: "",
        address: "",
      });
    }
  };

  // Create or Update Stationary
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let schoolIdToUse = selectedSchoolId;

      // If not using existing school, create a new school first
      if (!useExistingSchool || !selectedSchoolId) {
        const schoolRes = await fetch(SummaryApi.schools.create.url, {
          method: SummaryApi.schools.create.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });

        if (!schoolRes.ok) throw new Error("Failed to create school");

        const newSchool = await schoolRes.json();
        schoolIdToUse = newSchool._id;

        // Refresh schools list
        await fetchSchools();
      }

      // Now create or update the stationary with schoolId
      const stationaryData = {
        ...form,
        schoolId: schoolIdToUse,
      };

      const apiConfig = editingId
        ? SummaryApi.stationaries.update(editingId)
        : SummaryApi.stationaries.create;

      await fetch(apiConfig.url, {
        method: apiConfig.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(stationaryData),
      });

      setMessage(editingId ? "Stationary updated successfully!" : "Stationary created!");
      setShowModal(false);
      resetForm();
      fetchStationaries();
    } catch (err) {
      console.error("Error saving stationary:", err);
      setMessage("Failed to save stationary.");
    }
  };

  const resetForm = () => {
    setForm({
      schoolName: "",
      contact1: "",
      contact2: "",
      projectName: "",
      amount: "",
      address: "",
    });
    setEditingId(null);
    setUseExistingSchool(false);
    setSelectedSchoolId("");
  };

  // Edit existing stationary
  const handleEdit = (stationary) => {
    setForm({
      schoolName: stationary.schoolName,
      contact1: stationary.contact1,
      contact2: stationary.contact2 || "",
      projectName: stationary.projectName,
      amount: stationary.amount,
      address: stationary.address,
    });
    setEditingId(stationary._id);
    setSelectedSchoolId(stationary.schoolId || "");
    setUseExistingSchool(!!stationary.schoolId);
    setShowModal(true);
  };

  // Delete stationary
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this stationary?")) return;
    try {
      const apiConfig = SummaryApi.stationaries.delete(id);
      await fetch(apiConfig.url, { method: apiConfig.method });
      fetchStationaries();
    } catch (err) {
      console.error("Error deleting stationary:", err);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Stationary Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
        >
          + Add Stationary
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded mb-4 text-sm">
          {message}
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <div className="bg-white shadow rounded-lg overflow-x-auto">
  <table className="min-w-full text-sm text-left text-gray-700">
    <thead className="bg-gray-100 text-gray-800 uppercase text-xs">
      <tr>
        <th className="px-4 py-3">#</th>
        <th className="px-4 py-3">School Name</th>
        <th className="px-4 py-3">Contact 1</th>
        <th className="px-4 py-3">Project</th>
        <th className="px-4 py-3">Total Amount</th>
        <th className="px-4 py-3">Paid Amount</th>
        <th className="px-4 py-3">Pending Amount</th>
        <th className="px-4 py-3">Address</th>
        <th className="px-4 py-3 text-center">Actions</th>
      </tr>
    </thead>
    <tbody>
      {stationaries.length ? (
        stationaries.map((s, idx) => {
          const paid = getPaidAmount(s._id);
          const pending = getPendingAmount(s._id);
          return (
            <tr key={s._id} className="border-t hover:bg-gray-50">
              <td className="px-4 py-3">{idx + 1}</td>
              <td className="px-4 py-3">{s.schoolName}</td>
              <td className="px-4 py-3">{s.contact1}</td>
              <td className="px-4 py-3">{s.projectName}</td>
              <td className="px-4 py-3 font-medium">₹{s.amount}</td>
              <td className="px-4 py-3 text-green-700 font-semibold">₹{paid}</td>
              <td
                className={`px-4 py-3 font-semibold ${
                  pending > 0 ? "text-red-600" : "text-green-600"
                }`}
              >
                ₹{pending}
              </td>
              <td className="px-4 py-3">{s.address}</td>
              <td className="px-4 py-3 text-center flex gap-2 justify-center">
                <button
                  onClick={() =>
                    navigate(`/dashboard/manage-stationary-transactions/${s._id}`, {
                      state: { stationary: s },
                    })
                  }
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md"
                >
                  Manage
                </button>
                <button
                  onClick={() => handleEdit(s)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(s._id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                >
                  Delete
                </button>
              </td>
            </tr>
          );
        })
      ) : (
        <tr>
          <td colSpan="9" className="text-center py-4 text-gray-500">
            No stationaries yet.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {editingId ? "Edit Stationary" : "Add New Stationary"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Checkbox for existing school */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="useExistingSchool"
                  checked={useExistingSchool}
                  onChange={(e) => {
                    setUseExistingSchool(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedSchoolId("");
                      setForm({
                        schoolName: "",
                        contact1: "",
                        contact2: "",
                        projectName: "",
                        amount: "",
                        address: "",
                      });
                    }
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="useExistingSchool" className="text-sm font-medium text-gray-700">
                  Use Existing School
                </label>
              </div>

              {/* School dropdown - only show if checkbox is checked */}
              {useExistingSchool && (
                <div className="mb-3">
                  <select
                    value={selectedSchoolId}
                    onChange={handleSchoolSelect}
                    className="w-full border px-3 py-2 rounded-md"
                    required={useExistingSchool}
                  >
                    <option value="">Select a school...</option>
                    {schools.map((school) => (
                      <option key={school._id} value={school._id}>
                        {school.schoolName} - {school.projectName}
                      </option>
                    ))}
                  </select>
                  {selectedSchoolId && (
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ School details pre-filled. You can modify any field as needed.
                    </p>
                  )}
                </div>
              )}

              {/* Form fields - Always editable */}
              <input
                type="text"
                name="schoolName"
                placeholder="School Name"
                required
                value={form.schoolName}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="contact1"
                placeholder="Contact 1"
                required
                value={form.contact1}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="contact2"
                placeholder="Contact 2"
                value={form.contact2}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="projectName"
                placeholder="Project Name"
                required
                value={form.projectName}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                name="amount"
                placeholder="Amount"
                required
                value={form.amount}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                name="address"
                placeholder="Address"
                required
                value={form.address}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}